import { ComponentType } from '@angular/cdk/portal';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SideSheetService } from '../../../shared/side-sheet/side-sheet.service';
import { RecordDetailSheetComponent } from './record-detail-sheet.component';

/**
 * Shared open/close/toggle behaviour for the record columns, which show a row's detail in a side
 * sheet. `openDetailKey` stays the source of truth for row highlighting.
 *
 * The body is a parameter, not a fixture: a column that needs a real component — an editor, a
 * wizard — passes its own and gets the same toggle, swap and highlight behaviour. It defaults to
 * RecordDetailSheetComponent, which renders a plain RecordDetail, because that is what the three
 * columns need today.
 *
 * The service arrives through the constructor rather than injection: an Angular 8 base class that
 * injects must itself be decorated, so each column passes its own injected instance to super().
 */
export abstract class RecordDetailHost<TDetailKey> {
  public openDetailKey: TDetailKey = null;

  private openSheetReference: MatDialogRef<any, any> = null;

  constructor(protected sideSheetService: SideSheetService) {}

  public closeDetail() {
    this.openDetailKey = null;
    if (!this.openSheetReference) {
      return;
    }
    const closingReference = this.openSheetReference;
    this.openSheetReference = null;
    closingReference.close();
  }

  /**
   * Opens the row's body, or closes it if that same row is already open.
   *
   * @param buildBodyData produces whatever the body reads from MAT_DIALOG_DATA — a RecordDetail
   *        for the default renderer, anything the body expects for a custom one. Called lazily, so
   *        a re-click that closes the sheet builds nothing.
   * @param bodyComponent any component; it must be in its module's entryComponents.
   * @param config forwarded to the sheet — pass viewContainerRef here when the body is declared in
   *        a lazy module or needs the caller's providers.
   */
  protected toggleDetail<TData, TResult = void>(
    detailKey: TDetailKey,
    buildBodyData: () => TData,
    bodyComponent: ComponentType<any> = RecordDetailSheetComponent,
    config?: MatDialogConfig<TData>
  ): MatDialogRef<any, TResult> {
    if (this.openDetailKey === detailKey) {
      this.closeDetail();
      return null;
    }

    // Swapping rows: drop the previous sheet before opening the next one.
    this.closeDetail();
    this.openDetailKey = detailKey;

    const sheetReference = this.sideSheetService.open<any, TData, TResult>(
      bodyComponent,
      buildBodyData(),
      config
    );
    this.openSheetReference = sheetReference;

    // Keeps the row highlight honest when the sheet is dismissed by Escape or the backdrop rather
    // than by this class. The identity check matters: a swap closes the old sheet, whose exit
    // animation resolves AFTER the new one is already stored, and a blind reset would clear it.
    sheetReference.afterClosed().subscribe(() => {
      if (this.openSheetReference === sheetReference) {
        this.openSheetReference = null;
        this.openDetailKey = null;
      }
    });

    return sheetReference;
  }
}
